import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Phone, Mail, User, X } from 'lucide-react';

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

const PhoneBook = ({ isOpen, onClose }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchContacts();
    }
  }, [isOpen]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (isOpen) {
        fetchContacts();
      }
    }, 300);

    return () => clearTimeout(delaySearch);
  }, [searchQuery]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/phone-book/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: searchQuery ? { search: searchQuery } : {}
      });
      // handle case of array - to response data, handle paginated respones. 
      if (Array.isArray(response.data)) {
        setContacts(response.data);
        setError(null);
      } else if (response.data?.error) {
        setContacts([]);
        setError(response.data.error);
      } else if (response.data?.results) {
        // Handle paginated response = data results 
        setContacts(response.data.results);
        setError(null);
      } else {
        setContacts([]);
        setError('Unexpected response format');
      }
    } catch (err) {
      console.error('Error fetching phone book:', err);
      setContacts([]);
      setError('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };
  // handle - call phone number  - left side sidebar 
  const handleCall = (phoneNumber) => {
    if (phoneNumber) {
      window.location.href = `tel:${phoneNumber}`;
    }
  };
  // handle email 
  const handleEmail = (email) => {
    if (email) {
      window.location.href = `mailto:${email}`;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[100]">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col border border-slate-200">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-brand-50 to-white rounded-t-xl">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center">
              <Phone className="w-4 h-4 text-brand-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">Phone Book</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-slate-100 bg-slate-50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, phone or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white text-sm"
            />
          </div>
        </div>

        {/* Contacts List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-500 border-t-transparent"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-500 text-sm">{error}</div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm">
              {searchQuery ? 'No contacts found' : 'No contacts in your company'}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {contacts.map((contact) => (
                <div
                  key={contact.id}
                  className="p-4 hover:bg-brand-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {contact.profile_picture ? (
                        <img
                          src={contact.profile_picture}
                          alt={contact.full_name}
                          className="w-12 h-12 rounded-xl object-cover border border-slate-200"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-brand-100 flex items-center justify-center">
                          <User className="w-6 h-6 text-brand-600" />
                        </div>
                      )}
                    </div>

                    {/* Contact Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-slate-900 truncate">
                        {contact.full_name || `${contact.last_name} ${contact.first_name}`}
                      </h3>
                      {contact.phone_number && (
                        <p className="text-sm text-slate-600 truncate">
                          {contact.phone_number}
                        </p>
                      )}
                      {contact.company_email && (
                        <p className="text-xs text-slate-400 truncate">
                          {contact.company_email}
                        </p>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      {contact.phone_number && (
                        <button
                          onClick={() => handleCall(contact.phone_number)}
                          className="p-2.5 bg-emerald-100 text-emerald-600 rounded-lg hover:bg-emerald-200 transition-colors"
                          title={`Call ${contact.phone_number}`}
                        >
                          <Phone className="w-4 h-4" />
                        </button>
                      )}
                      {contact.company_email && (
                        <button
                          onClick={() => handleEmail(contact.company_email)}
                          className="p-2.5 bg-brand-100 text-brand-600 rounded-lg hover:bg-brand-200 transition-colors"
                          title={`Email ${contact.company_email}`}
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <p className="text-xs text-center text-slate-500 font-medium">
            {contacts.length} contact{contacts.length !== 1 ? 's' : ''} in your company
          </p>
        </div>
      </div>
    </div>
  );
};

export default PhoneBook;
